import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FormField,
  Input,
  Logo,
  Skeleton,
  formatDateTimeVn,
  useToast,
} from "@chotca/ui";
import { ChotcaApiError } from "@chotca/sdk";
import { getSdk } from "@chotca/api-client";
import { setAccessToken } from "@/boot/access-token";
import { WEB_APEX_URL } from "@/env";
import { errMsg } from "./auth-errors";

/** Lightweight Vietnamese relative time for the applicant teaser. */
function relativeVn(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "vừa xong";
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.round(hours / 24);
  if (days === 1) return "hôm qua";
  if (days < 7) return `${days} ngày trước`;
  return formatDateTimeVn(new Date(iso));
}

function ArrowUpRight({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

export function ClaimPage() {
  const { token = "" } = useParams<{ token: string }>();
  const t = useTranslations("claim");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const previewQ = useQuery({
    queryKey: ["claim", "preview", token],
    queryFn: async () => getSdk().admin.claimPreview(token),
    enabled: token.length > 0,
    retry: false,
  });

  if (!token) {
    return (
      <CenterCard>
        <p className="text-sm font-semibold text-rose-700">
          {t("errorNotFound")}
        </p>
      </CenterCard>
    );
  }

  if (previewQ.isLoading) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-stretch justify-center gap-3 p-6 lg:max-w-4xl">
        <Skeleton className="h-72 rounded-3xl" />
      </main>
    );
  }
  if (previewQ.isError) {
    const e = previewQ.error;
    let msg = t("errorNotFound");
    if (e instanceof ChotcaApiError) {
      if (e.status === 410) msg = t("errorExpired");
      else if (e.status === 409) msg = t("errorClaimed");
    }
    return (
      <CenterCard>
        <p className="text-sm font-semibold text-rose-700">{msg}</p>
      </CenterCard>
    );
  }
  const preview = previewQ.data!;

  if (done) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-stretch justify-center p-6">
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <h1 className="text-xl font-bold text-emerald-800">
            {t("successTitle")}
          </h1>
          <p className="mt-2 text-sm text-emerald-700">{t("successDesc")}</p>
        </div>
      </main>
    );
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const password = String(fd.get("password") ?? "");
    const fullName = String(fd.get("fullName") ?? "").trim();
    try {
      const res = await getSdk().admin.claimAccept(token, {
        password,
        fullName,
      });
      if ("accessToken" in res && res.accessToken) {
        setAccessToken(res.accessToken as string);
      }
      qc.invalidateQueries({ queryKey: ["me"] });
      setDone(true);
      // New owners land on the onboarding/welcome screen, not the cold dashboard.
      setTimeout(() => nav("/welcome", { replace: true }), 1000);
    } catch (err) {
      const msg = errMsg(err, "Không bắt đầu được, vui lòng thử lại");
      toast({ tone: "error", title: msg });
    } finally {
      setSubmitting(false);
    }
  }

  const count = preview.applicantCount;
  const hasCandidates = count > 0;
  const orgLocation = [preview.orgDistrict, preview.orgCity]
    .filter(Boolean)
    .join(", ");

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-mist p-4 sm:p-6">
      <div className="w-full max-w-md lg:max-w-4xl">
        <div className="overflow-hidden rounded-3xl border border-smoke bg-paper shadow-[0_30px_80px_-30px_rgba(0,0,0,0.3)] lg:grid lg:grid-cols-[1.05fr_0.95fr]">
          {/* ── Proof-of-value pane (dark) ───────────────────────── */}
          <div className="relative overflow-hidden bg-[radial-gradient(130%_120%_at_0%_0%,#16402f_0%,#0f0f0f_60%)] p-6 text-white lg:p-8">
            <span
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-lime/20 blur-2xl"
            />
            <div className="relative">
              <Logo variant="horizontal" size={30} onDark />

              {hasCandidates && (
                <span className="mt-7 inline-flex items-center gap-1.5 rounded-full border border-lime/25 bg-lime/15 px-3 py-1.5 text-xs font-bold text-lime">
                  🔥 {t("heroPill")}
                </span>
              )}

              <h1 className="mt-4 text-[22px] font-extrabold leading-snug tracking-tight lg:text-[26px]">
                {hasCandidates
                  ? t("heroTitleWithCount", {
                      orgName: preview.orgName,
                      count,
                    })
                  : t("heroTitleNoCount", { orgName: preview.orgName })}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                {t("heroSubtitle")}
              </p>

              {(hasCandidates || preview.openPostingCount > 0) && (
                <div className="mt-6 flex gap-2.5">
                  <Stat value={count} label={t("statApplicants")} />
                  <Stat
                    value={preview.openPostingCount}
                    label={t("statPostings")}
                  />
                </div>
              )}

              {/* Org identity */}
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-white/15 bg-white/8 text-lg">
                  🏪
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-bold">
                    {preview.orgName}
                  </div>
                  {orgLocation && (
                    <div className="truncate text-[13px] text-white/50">
                      {orgLocation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Action pane (light) ──────────────────────────────── */}
          <div className="flex flex-col p-6 lg:p-8">
            <div>
              <h2 className="text-lg font-extrabold tracking-tight text-ink">
                {t("takeoverTitle")}
              </h2>
              <p className="mt-1 text-[13px] text-ash">
                {t("takeoverSubtitle")}
              </p>
            </div>

            {/* Postings with pending applicants — each links to public post */}
            {preview.postings.length > 0 && (
              <div className="mt-5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-ash">
                    {t("postingsLabel")}
                  </span>
                  {hasCandidates && (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-lime-dark">
                      {t("applicantCountChip", { count })}
                    </span>
                  )}
                </div>
                <div>
                  {preview.postings.map((p) => (
                    <a
                      key={p.postingId}
                      href={`${WEB_APEX_URL}/postings/${p.postingId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-3 border-t border-[#f1f1ef] py-3 first:border-t-0"
                    >
                      <span className="flex h-11 w-11 flex-none flex-col items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 leading-none text-emerald-700">
                        <b className="text-base">{p.applicantCount}</b>
                        <small className="text-[8.5px] font-bold tracking-wide text-lime-dark">
                          {t("postingApplicantBadge")}
                        </small>
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-bold text-ink group-hover:text-lime-dark">
                          <span className="truncate">{p.title}</span>
                          <ArrowUpRight className="flex-none opacity-40 group-hover:opacity-100" />
                        </div>
                        <div className="mt-0.5 truncate text-xs text-ash">
                          {t("postingLatest", {
                            when: relativeVn(p.latestAppliedAt),
                          })}{" "}
                          · {t("postingViewLink")}
                        </div>
                      </div>
                      <svg
                        className="ml-auto flex-none text-ash"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Perk */}
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-[linear-gradient(100deg,#ecfdf5,#f5fffa)] px-3.5 py-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-lime text-lg shadow-[0_6px_16px_-6px_rgba(50,255,170,0.7)]">
                🎁
              </span>
              <div>
                <b className="text-sm text-ink">{t("perkTitle")}</b>
                <div className="text-xs text-emerald-700">
                  {t("perkSubtitle")}
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="mt-6 space-y-3 lg:mt-auto">
              <FormField label={t("fullNameLabel")}>
                <Input name="fullName" required />
              </FormField>
              <FormField label={t("passwordLabel")}>
                <Input name="password" type="password" required minLength={8} />
              </FormField>
              <Button type="submit" loading={submitting} className="w-full">
                {t("submit")}
              </Button>
              <p className="text-center text-[11px] leading-relaxed text-ash">
                {t("sentTo", { email: preview.email })} · {t("expiresAt")}{" "}
                {formatDateTimeVn(new Date(preview.expiresAt))}
              </p>
            </form>
          </div>
        </div>

        <p className="px-2 pb-1 pt-4 text-center text-[11px] leading-relaxed text-[#a1a1aa]">
          {t("foot")}
        </p>
      </div>
    </main>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex-1 rounded-2xl border border-white/10 bg-white/6 px-3.5 py-3">
      <div className="text-2xl font-extrabold leading-none">{value}</div>
      <div className="mt-1.5 text-xs text-white/55">{label}</div>
    </div>
  );
}

function CenterCard({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-stretch justify-center p-6">
      <div className="rounded-3xl border border-rose-200 bg-rose-50 p-8 text-center">
        {children}
      </div>
    </main>
  );
}
